import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const HomeContainer = styled.main`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Hero = styled.section`
  display: flex;
  align-items: center;
  background: linear-gradient(rgba(245,245,220,0.8), rgba(245,245,220,0.8)), url('https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=1200') center/cover;
  padding: 4rem 2rem;
  border-radius: 10px;
  margin-bottom: 3rem;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    padding: 2rem 1rem;
  }
`;

const HeroContent = styled.div`
  flex: 1;
  padding-right: 2rem;

  @media (max-width: 768px) {
    padding-right: 0;
    margin-bottom: 2rem;
  }

  h2 {
    font-size: 2.5rem;
    margin-bottom: 1rem;
    color: #8b4513;

    @media (max-width: 768px) {
      font-size: 2rem;
    }
  }

  p {
    font-size: 1.2rem;
    margin-bottom: 2rem;
  }
`;

const HeroImage = styled.div`
  flex: 1;
  text-align: center;

  img {
    max-width: 100%;
    border-radius: 10px;
    box-shadow: 0 5px 15px rgba(0,0,0,0.2);
  }
`;

const Button = styled(Link)`
  display: inline-block;
  background-color: #d2691e;
  color: white;
  padding: 10px 20px;
  border-radius: 5px;
  transition: background-color 0.3s;
  text-decoration: none;

  &:hover {
    background-color: #a0522d;
  }
`;

const Featured = styled.section`
  margin-bottom: 3rem;

  h2 {
    text-align: center;
    margin-bottom: 2rem;
    color: #8b4513;
  }
`;

const CakesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
`;

const CakeItem = styled(motion.div)`
  background-color: white;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0,0,0,0.1);
  transition: transform 0.3s;

  &:hover {
    transform: translateY(-5px);
  }

  img {
    width: 100%;
    height: 200px;
    object-fit: cover;
  }

  h3 {
    padding: 1rem;
    font-size: 1.3rem;
    color: #8b4513;
  }

  p {
    padding: 0 1rem 1rem;
    color: #666;
  }
`;

const AboutPreview = styled.section`
  background-color: #f9f9f9;
  padding: 3rem;
  border-radius: 10px;
  text-align: center;
  margin-bottom: 3rem;

  h2 {
    margin-bottom: 1rem;
    color: #8b4513;
  }

  p {
    font-size: 1.1rem;
    margin-bottom: 2rem;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const Services = styled.section`
  margin-bottom: 3rem;

  h2 {
    text-align: center;
    margin-bottom: 2rem;
    color: #8b4513;
  }
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
`;

const Service = styled(motion.div)`
  background-color: white;
  padding: 2rem;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0,0,0,0.1);
  text-align: center;

  h3 {
    color: #8b4513;
    margin-bottom: 1rem;
  }

  p {
    color: #666;
    line-height: 1.6;
  }
`;

const Testimonials = styled.section`
  margin-bottom: 3rem;

  h2 {
    text-align: center;
    margin-bottom: 2rem;
    color: #8b4513;
  }
`;

const TestimonialsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
`;

const Testimonial = styled(motion.div)`
  background-color: white;
  padding: 2rem;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0,0,0,0.1);
  text-align: center;

  p {
    font-style: italic;
    margin-bottom: 1rem;
    font-size: 1.1rem;
  }

  cite {
    font-weight: bold;
    color: #d2691e;
  }
`;

const Home = () => {
  const featuredCakes = [
    {
      id: 1,
      image: 'https://images.unsplash.com/photo-1621303837174-89787a7d4729?w=400',
      title: 'Wedding Cakes',
      description: 'Elegant designs for your special day.'
    },
    {
      id: 2,
      image: 'https://images.unsplash.com/photo-1551024506-0bccd828d307?w=400',
      title: 'Birthday Cakes',
      description: 'Fun and festive for all ages.'
    },
    {
      id: 3,
      image: 'https://images.unsplash.com/photo-1464349095431-e9a21285b5f3?w=400',
      title: 'Specialty Desserts',
      description: 'Unique flavors and presentations.'
    }
  ];

  const services = [
    {
      id: 1,
      title: 'Custom Cake Design',
      description: 'Bring your vision to life with our personalized cake design service. From themed cakes to intricate decorations, we create edible art for any occasion.'
    },
    {
      id: 2,
      title: 'Wedding Cakes',
      description: 'Elegant multi-tiered cakes that serve as the centerpiece of your special day. We work closely with you to match your wedding theme and colors.'
    },
    {
      id: 3,
      title: 'Cupcakes & Desserts',
      description: 'Delicious cupcakes, cookies, and petit fours perfect for parties, showers, or as thoughtful gifts. Available in various flavors and designs.'
    },
    {
      id: 4,
      title: 'Corporate Events',
      description: 'Impress your clients and colleagues with our professional dessert displays and custom treats for meetings, conferences, and celebrations.'
    },
    {
      id: 5,
      title: 'Cake Workshops',
      description: 'Learn the art of cake decorating in our hands-on workshops. Perfect for beginners and experienced bakers looking to hone their skills.'
    },
    {
      id: 6,
      title: 'Delivery Service',
      description: 'Reliable delivery service to ensure your desserts arrive fresh and beautifully presented. We serve the local area with care and attention.'
    }
  ];

  const testimonials = [
    {
      id: 1,
      text: '"The wedding cake was absolutely stunning! Every guest raved about it, and it tasted as beautiful as it looked."',
      author: 'Sarah & John, Wedding Clients'
    },
    {
      id: 2,
      text: '"My daughter\'s birthday cake was a masterpiece. The attention to detail and creativity exceeded our expectations."',
      author: 'Maria Rodriguez, Happy Parent'
    },
    {
      id: 3,
      text: '"Sweet Delights made our corporate event unforgettable. The dessert display was both elegant and delicious."',
      author: 'TechCorp Events Team'
    }
  ];

  return (
    <HomeContainer>
      <Hero as={motion.section} initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
        <HeroContent>
          <h2>Custom Cakes & Desserts Made with Love</h2>
          <p>Indulge in our handcrafted creations, perfect for every occasion.</p>
          <Button to="/gallery">View Our Gallery</Button>
        </HeroContent>
        <HeroImage>
          <img src="https://images.unsplash.com/photo-1578985545062-69928b1d9587?w=800" alt="Beautiful custom cake" />
        </HeroImage>
      </Hero>

      <Featured>
        <h2>Featured Creations</h2>
        <CakesGrid>
          {featuredCakes.map((cake, index) => (
            <CakeItem
              key={cake.id}
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ scale: 1.05 }}
            >
              <img src={cake.image} alt={cake.title} />
              <h3>{cake.title}</h3>
              <p>{cake.description}</p>
            </CakeItem>
          ))}
        </CakesGrid>
      </Featured>

      <AboutPreview as={motion.section} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
        <h2>Why Choose Us?</h2>
        <p>At Sweet Delights Bakery, we specialize in creating custom cakes and desserts that tell your story. Using the finest ingredients and artistic flair, we bring your visions to life.</p>
        <Button to="/about">Learn More</Button>
      </AboutPreview>

      <Services>
        <h2>Our Services</h2>
        <ServicesGrid>
          {services.map((service, index) => (
            <Service
              key={service.id}
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ scale: 1.05 }}
            >
              <h3>{service.title}</h3>
              <p>{service.description}</p>
            </Service>
          ))}
        </ServicesGrid>
      </Services>

      <Testimonials>
        <h2>What Our Customers Say</h2>
        <TestimonialsGrid>
          {testimonials.map((testimonial, index) => (
            <Testimonial
              key={testimonial.id}
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ scale: 1.05 }}
            >
              <p>{testimonial.text}</p>
              <cite>{testimonial.author}</cite>
            </Testimonial>
          ))}
        </TestimonialsGrid>
      </Testimonials>
    </HomeContainer>
  );
};

export default Home;